import { BUTTER, CORAL, INK, LILAC, MINT, PAPER, PEACH, SKY, lerpColor } from '../../color';
import type { World as Look } from '../../world';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, spots, starPath, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const HORIZONS = [CORAL, LILAC, BUTTER];
const WALLS = [PEACH, MINT, SKY, BUTTER, LILAC, CORAL];
const FLAGS = [CORAL, BUTTER, MINT, SKY, LILAC, PEACH];
const BULB_GAP = 86;

function sky(p: Painter, look: Look): void {
  const ctx = p.ctx;
  const bg = look.palette.bg;
  const horizon = HORIZONS[(p.level - 1) % HORIZONS.length] ?? CORAL;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(bg, 0x000000, 0.25)));
  g.addColorStop(0.55, rgba(lerpColor(bg, LILAC, 0.14)));
  g.addColorStop(1, rgba(lerpColor(bg, horizon, 0.32)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < 46; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h * 0.6;
    if (calmAt(p, x, y, 0.9)) continue;
    ctx.fillStyle = rgba(PAPER, p.rng.range(0.25, 0.6));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.5), 0, TAU);
    ctx.fill();
  }
}

function moon(p: Painter): void {
  const ctx = p.ctx;
  const r = Math.max(26, p.unit * 0.1);
  const x = Math.min(p.w * 0.14, p.cx - p.unit * 1.05);
  const y = Math.max(r * 2.2, p.h * 0.14);
  glowDot(ctx, x, y, r * 4, BUTTER, 0.16);
  ctx.fillStyle = rgba(lerpColor(PAPER, BUTTER, 0.35), 0.85);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(PAPER, LILAC, 0.5), 0.5);
  for (const [dx, dy, k] of [
    [-0.3, -0.2, 0.22],
    [0.25, 0.1, 0.16],
    [-0.05, 0.4, 0.12],
  ] as const) {
    ctx.beginPath();
    ctx.arc(x + dx * r, y + dy * r, k * r, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = rgba(INK, 0.35);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.stroke();
}

function hill(p: Painter, side: -1 | 1, base: number, height: number, color: number): number[] {
  const ctx = p.ctx;
  const x0 = side < 0 ? -20 : p.w + 20;
  const x1 = side < 0 ? p.w * 0.42 : p.w * 0.58;
  const top: number[] = [];
  ctx.fillStyle = rgba(color);
  ctx.beginPath();
  ctx.moveTo(x0, p.h);
  for (let i = 0; i <= 24; i++) {
    const t = i / 24;
    const x = x0 + (x1 - x0) * t;
    const y = base - height * Math.pow(1 - t, 1.3) * (0.92 + 0.08 * Math.sin(t * 9 + side));
    top.push(x, y);
    ctx.lineTo(x, y);
  }
  ctx.lineTo(x1, p.h);
  ctx.closePath();
  ctx.fill();
  return top;
}

function houses(p: Painter, top: number[], wall: number, night: number): void {
  const ctx = p.ctx;
  for (let i = 0; i + 3 < top.length; i += 2) {
    if (!p.rng.chance(0.7)) continue;
    const x = top[i] ?? 0;
    const y = (top[i + 1] ?? 0) + p.rng.range(4, 14);
    const w = p.rng.range(18, 34) + p.unit * 0.02;
    const h = p.rng.range(14, 26) + p.unit * 0.02;
    const tone = lerpColor(night, p.rng.pick(WALLS), wall);
    ctx.fillStyle = rgba(tone);
    ctx.fillRect(x - w / 2, y - h, w, h + 40);
    ctx.fillStyle = rgba(lerpColor(tone, 0x000000, 0.25));
    ctx.fillRect(x - w / 2 - 2, y - h - 3, w + 4, 4);
    if (p.rng.chance(0.55)) {
      ctx.fillStyle = rgba(lerpColor(tone, BUTTER, 0.4));
      ctx.fillRect(x - w * 0.25, y - h * 0.65, w * 0.2, h * 0.3);
    }
    if (p.rng.chance(0.3)) {
      ctx.fillStyle = rgba(lerpColor(night, SKY, wall * 0.9));
      ctx.fillRect(x + w * 0.12, y - h - 11, 10, 8);
      ctx.beginPath();
      ctx.ellipse(x + w * 0.12 + 5, y - h - 11, 5, 2, 0, 0, TAU);
      ctx.fill();
    } else if (p.rng.chance(0.25)) {
      ctx.strokeStyle = rgba(lerpColor(night, PAPER, 0.25));
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - w * 0.3, y - h);
      ctx.lineTo(x - w * 0.3, y - h - 16);
      ctx.moveTo(x - w * 0.3 - 6, y - h - 12);
      ctx.lineTo(x - w * 0.3 + 6, y - h - 12);
      ctx.stroke();
    }
  }
}

function church(p: Painter, x: number, y: number, night: number): void {
  const ctx = p.ctx;
  const s = Math.max(14, p.unit * 0.05);
  ctx.fillStyle = rgba(lerpColor(night, PAPER, 0.22));
  ctx.fillRect(x - s, y - s * 1.4, s * 2, s * 1.4 + 20);
  ctx.beginPath();
  ctx.moveTo(x - s * 1.15, y - s * 1.4);
  ctx.lineTo(x, y - s * 2.2);
  ctx.lineTo(x + s * 1.15, y - s * 1.4);
  ctx.fill();
  ctx.fillRect(x - s * 0.08, y - s * 2.9, s * 0.16, s * 0.75);
  ctx.fillRect(x - s * 0.3, y - s * 2.65, s * 0.6, s * 0.14);
  ctx.fillStyle = rgba(lerpColor(night, BUTTER, 0.45));
  ctx.beginPath();
  ctx.arc(x, y - s * 0.75, s * 0.3, Math.PI, 0);
  ctx.lineTo(x + s * 0.3, y);
  ctx.lineTo(x - s * 0.3, y);
  ctx.fill();
}

function lights(p: Painter, x0: number, y0: number, x1: number, y1: number, sagBy: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(INK, PAPER, 0.2), 0.8);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let i = 0; i <= 30; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / 30);
    if (i === 0) ctx.moveTo(at.x, at.y);
    else ctx.lineTo(at.x, at.y);
  }
  ctx.stroke();
  const span = Math.hypot(x1 - x0, y1 - y0);
  const count = Math.max(2, Math.floor(span / BULB_GAP));
  for (let i = 1; i < count; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / count);
    if (calmAt(p, at.x, at.y, 0.95)) continue;
    const tone = FLAGS[(i + p.level) % FLAGS.length] ?? BUTTER;
    glowDot(ctx, at.x, at.y + 6, 26, tone, 0.2);
    ctx.fillStyle = rgba(lerpColor(INK, 0x000000, 0.3), 0.9);
    ctx.fillRect(at.x - 2.5, at.y - 1, 5, 5);
    ctx.fillStyle = rgba(lerpColor(tone, PAPER, 0.35), 0.9);
    ctx.beginPath();
    ctx.ellipse(at.x, at.y + 8, 4.2, 5.6, 0, 0, TAU);
    ctx.fill();
  }
}

function flags(p: Painter, x0: number, y0: number, x1: number, y1: number, night: number): void {
  const ctx = p.ctx;
  const span = Math.hypot(x1 - x0, y1 - y0);
  const size = Math.max(16, p.unit * 0.055);
  const count = Math.max(3, Math.floor(span / (size * 1.15)));
  const sagBy = span * 0.08;
  ctx.strokeStyle = rgba(lerpColor(night, PAPER, 0.3));
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 20; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / 20);
    if (i === 0) ctx.moveTo(at.x, at.y);
    else ctx.lineTo(at.x, at.y);
  }
  ctx.stroke();
  for (let i = 0; i < count; i++) {
    const a = catenary(x0, y0, x1, y1, sagBy, (i + 0.1) / count);
    const b = catenary(x0, y0, x1, y1, sagBy, (i + 0.9) / count);
    const tone = lerpColor(night, FLAGS[(i * 2 + p.level) % FLAGS.length] ?? CORAL, 0.42);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    ctx.fillStyle = rgba(tone, 0.95);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.lineTo(mx + p.rng.range(-2, 2), my + size * 1.05);
    ctx.lineTo(mx, my + size * 0.72);
    ctx.closePath();
    ctx.fill();
  }
}

function speaker(p: Painter, x: number, y: number, night: number): void {
  const ctx = p.ctx;
  const s = Math.max(40, p.unit * 0.16);
  ctx.fillStyle = rgba(lerpColor(night, INK, 0.5));
  ctx.fillRect(x - s * 0.5, y - s * 1.3, s, s * 1.3 + 20);
  ctx.strokeStyle = rgba(lerpColor(night, PAPER, 0.18));
  ctx.lineWidth = 2;
  ctx.strokeRect(x - s * 0.5, y - s * 1.3, s, s * 1.3 + 20);
  for (const [cy, r] of [
    [-0.95, 0.16],
    [-0.45, 0.3],
  ] as const) {
    ctx.fillStyle = rgba(lerpColor(night, 0x000000, 0.35));
    ctx.beginPath();
    ctx.arc(x, y + cy * s, r * s, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(lerpColor(night, LILAC, 0.3));
    ctx.beginPath();
    ctx.arc(x, y + cy * s, r * s, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y + cy * s, r * s * 0.35, 0, TAU);
    ctx.stroke();
  }
}

function plant(p: Painter, x: number, y: number, night: number): void {
  const ctx = p.ctx;
  const s = Math.max(40, p.unit * 0.16);
  ctx.fillStyle = rgba(lerpColor(night, CORAL, 0.35));
  ctx.beginPath();
  ctx.moveTo(x - s * 0.4, y - s * 0.5);
  ctx.lineTo(x + s * 0.4, y - s * 0.5);
  ctx.lineTo(x + s * 0.3, y + 10);
  ctx.lineTo(x - s * 0.3, y + 10);
  ctx.closePath();
  ctx.fill();
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI / 2 + (i - 3) * 0.32;
    const len = s * p.rng.range(0.8, 1.25);
    const tipX = x + Math.cos(a) * len;
    const tipY = y - s * 0.5 + Math.sin(a) * len;
    ctx.fillStyle = rgba(lerpColor(night, MINT, p.rng.range(0.22, 0.34)));
    ctx.beginPath();
    ctx.moveTo(x, y - s * 0.5);
    ctx.quadraticCurveTo(x + Math.cos(a + 0.5) * len * 0.6, y - s * 0.5 + Math.sin(a + 0.5) * len * 0.6, tipX, tipY);
    ctx.quadraticCurveTo(x + Math.cos(a - 0.5) * len * 0.6, y - s * 0.5 + Math.sin(a - 0.5) * len * 0.6, x, y - s * 0.5);
    ctx.fill();
  }
}

function confetti(p: Painter, night: number): void {
  const ctx = p.ctx;
  for (const at of spots(p, 38, 1.05, 10, 22)) {
    const tone = lerpColor(night, p.rng.pick(FLAGS), 0.5);
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.rotate(p.rng.float() * TAU);
    ctx.fillStyle = rgba(tone, 0.8);
    if (p.rng.chance(0.3)) {
      starPath(ctx, 0, 0, 4, 0.45, 5, 0);
      ctx.fill();
    } else if (p.rng.chance(0.5)) ctx.fillRect(-4, -2, 8, 4);
    else {
      ctx.beginPath();
      ctx.arc(0, 0, 2.6, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }
}

export function paint(p: Painter, look: Look): void {
  const night = look.palette.bg;
  sky(p, look);
  moon(p);
  const far = lerpColor(night, LILAC, 0.14);
  const near = lerpColor(night, 0x000000, 0.15);
  const backL = hill(p, -1, p.h * 0.92, p.h * 0.34, far);
  const backR = hill(p, 1, p.h * 0.95, p.h * 0.3, far);
  houses(p, backL, 0.2, far);
  houses(p, backR, 0.2, far);
  church(p, backR[4] ?? p.w * 0.9, (backR[5] ?? p.h * 0.7) + 6, far);
  const frontL = hill(p, -1, p.h * 1.02, p.h * 0.2, near);
  const frontR = hill(p, 1, p.h * 1.04, p.h * 0.18, near);
  houses(p, frontL, 0.28, near);
  houses(p, frontR, 0.28, near);
  lights(p, -10, p.h * 0.06, p.w * 0.5, p.h * 0.02, p.h * 0.1);
  lights(p, p.w * 0.5, p.h * 0.02, p.w + 10, p.h * 0.08, p.h * 0.09);
  lights(p, -10, p.h * 0.34, p.w * 0.18, p.h * 0.02, p.h * 0.05);
  lights(p, p.w * 0.82, p.h * 0.02, p.w + 10, p.h * 0.38, p.h * 0.05);
  flags(p, -10, p.h * 0.16, p.w * 0.3, -6, night);
  flags(p, p.w * 0.72, -6, p.w + 10, p.h * 0.18, night);
  confetti(p, night);
  speaker(p, Math.max(38, p.w * 0.06), p.h - 6, night);
  plant(p, p.w - Math.max(40, p.w * 0.06), p.h - 6, night);
  calmCenter(p, night, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, 0x000000, 0.45);
}
